import { Link } from "react-router-dom";
import { ShieldCheck, Mail, Database, UserCheck, Clock, Share2, Cookie, Scale } from "lucide-react";
import { PublicLayout } from "../components/PublicLayout";
import { Card, CardContent } from "../components/ui/card";
import { useLang } from "../context/LanguageContext";

const Section = ({ icon: Icon, title, children, testId }) => (
  <section className="space-y-2" data-testid={testId}>
    <h2 className="text-lg font-heading font-semibold flex items-center gap-2">
      <Icon className="w-4.5 h-4.5 text-muted-foreground" aria-hidden="true" />
      {title}
    </h2>
    <div className="text-sm text-muted-foreground leading-relaxed space-y-2">{children}</div>
  </section>
);

export default function PrivacyPage() {
  const { t } = useLang();
  return (
    <PublicLayout>
      <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <div className="mb-8">
          <div className="inline-flex items-center gap-2 rounded-full border border-border bg-secondary/60 px-3 py-1 text-xs text-muted-foreground mb-4" data-testid="privacy-badge">
            <ShieldCheck className="w-3.5 h-3.5" />
            {t("GDPR-samsvar")}
          </div>
          <h1 className="text-3xl sm:text-4xl font-heading font-semibold tracking-tight" data-testid="privacy-title">
            {t("Personvernerklæring")}
          </h1>
          <p className="text-muted-foreground mt-2">
            {t("Right in («vi») tar personvernet ditt på alvor. Her forklarer vi hvilke opplysninger vi samler inn, hvorfor, og hvilke rettigheter du har etter personvernforordningen (GDPR) og personopplysningsloven.")}
          </p>
          <p className="text-xs text-muted-foreground mt-2" data-testid="privacy-updated">{t("Sist oppdatert: september 2026")}</p>
        </div>

        <Card className="border-border">
          <CardContent className="p-6 sm:p-8 space-y-8">
            <Section icon={Database} title={t("Hvilke opplysninger vi samler inn")} testId="privacy-section-data">
              <p><strong className="text-foreground">{t("Billettkjøpere:")}</strong> {t("E-postadresse (påkrevd for å levere billettene). Navn og telefonnummer er valgfritt og lagres kun hvis du oppgir det.")}</p>
              <p><strong className="text-foreground">{t("Arrangører:")}</strong> {t("Navn, e-postadresse, passord (kryptert) og organisasjonsinformasjon hentet fra Brønnøysundregistrene (offentlig tilgjengelig).")}</p>
              <p><strong className="text-foreground">{t("Venteliste:")}</strong> {t("Navn og e-postadresse, kun for å varsle deg når billetter blir ledige.")}</p>
              <p><strong className="text-foreground">{t("Posisjon:")}</strong> {t("Hvis du gir samtykke i nettleseren, brukes posisjonen din kun til å vise arrangementer nær deg. Den lagres aldri hos oss.")}</p>
            </Section>

            <Section icon={Scale} title={t("Formål og rettslig grunnlag")} testId="privacy-section-basis">
              <p>{t("Vi behandler opplysningene for å levere billetter, gjennomføre innsjekk med QR-kode, sende bekreftelser og påminnelser om arrangementer du har billetter til, og varsle venteliste. Rettslig grunnlag er oppfyllelse av avtale (GDPR art. 6 nr. 1 b).")}</p>
              <p>{t("Arrangøren av et arrangement er behandlingsansvarlig for deltakeropplysningene til sitt arrangement. Right in behandler opplysningene på vegne av arrangøren.")}</p>
            </Section>

            <Section icon={Share2} title={t("Deling av opplysninger")} testId="privacy-section-sharing">
              <p>{t("Vi selger aldri opplysningene dine. Opplysninger deles kun med:")}</p>
              <ul className="list-disc pl-5 space-y-1">
                <li><strong className="text-foreground">{t("Arrangøren")}</strong> {t("av arrangementet du kjøper billett til (navn, e-post, telefon og antall billetter, for deltakerliste og innsjekk).")}</li>
                <li><strong className="text-foreground">{t("E-postleverandør")}</strong> {t("(Brevo) for utsending av billetter, bekreftelser og påminnelser.")}</li>
                <li><strong className="text-foreground">{t("Betalingsleverandør")}</strong> {t("(Vipps MobilePay) ved kjøp av betalte billetter. Vi lagrer aldri betalingskortinformasjon.")}</li>
              </ul>
            </Section>

            <Section icon={Clock} title={t("Lagringstid")} testId="privacy-section-retention">
              <p>{t("Billett- og bestillingsopplysninger lagres så lenge det er nødvendig for gjennomføring av arrangementet og eventuell regnskapsplikt hos arrangøren. Ventelisteoppføringer slettes etter at arrangementet er gjennomført. Du kan når som helst be om sletting av dine opplysninger.")}</p>
            </Section>

            <Section icon={UserCheck} title={t("Dine rettigheter")} testId="privacy-section-rights">
              <p>{t("Etter GDPR har du rett til innsyn, retting, sletting («retten til å bli glemt»), begrensning av behandling, dataportabilitet og å protestere mot behandling. Kontakt oss, så hjelper vi deg – normalt innen 30 dager.")}</p>
              <p>{t("Du har også rett til å klage til")} <a href="https://www.datatilsynet.no" target="_blank" rel="noopener noreferrer" className="underline underline-offset-4 text-foreground" data-testid="privacy-datatilsynet-link">Datatilsynet</a> {t("hvis du mener behandlingen bryter med regelverket.")}</p>
            </Section>

            <Section icon={Cookie} title={t("Informasjonskapsler og lokal lagring")} testId="privacy-section-cookies">
              <p>{t("Vi bruker ingen sporings- eller markedsføringscookies. Nettleserens lokale lagring brukes kun til nødvendig funksjonalitet: innloggingsøkt for arrangører, språkvalg og å huske at du har sett et arrangement (for korrekt besøksstatistikk).")}</p>
            </Section>

            <Section icon={Mail} title={t("Kontakt oss")} testId="privacy-section-contact">
              <p>
                {t("Har du spørsmål om personvern, eller ønsker du innsyn eller sletting? Send en e-post til")}{" "}
                <a href="mailto:hei@rightin.no" className="underline underline-offset-4 text-foreground" data-testid="privacy-contact-email">hei@rightin.no</a>.
              </p>
            </Section>
          </CardContent>
        </Card>

        <p className="text-xs text-muted-foreground mt-6">
          <Link to="/" className="underline underline-offset-4" data-testid="privacy-home-link">{t("Tilbake til forsiden")}</Link>
        </p>
      </div>
    </PublicLayout>
  );
}
